import React, { useCallback, useEffect, useRef, useState } from "react";
import ServerRequest from "../../exports/ServerRequest";
import { GetCookie } from "../../exports/CookieManagement";
import Pulse from "../Loader/Pulse";
import { ArrowRight2, OrderBook } from "../CustomComponents/SwiftIcon/Icons";
import { COLOR_VARS } from "../../exports/ColorVars";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import { priceFormat } from "../../exports/UtilFunctions";
import SwiftModal from "../CustomComponents/SwiftModal/SwiftModal";
import CustomIconButton from "../Body/CustomChartComponents/CustomIconButton/CustomIconButton";
import { ConfirmationBoxSmart } from "./ConfirmationBox";
import Badge from "../ui/badge";

const DEVICE_SIZE = getDeviceSize();

const SipTransaction = ({ customer }) => {
  const [loading, setLoading] = useState(true);
  const [investment, setInvestment] = useState([]);
  const [loadingMsg, setLoadingMsg] = useState("Fetching your SIPs");
  const [opacityMsg, setOpacityMsg] = useState(true);
  const [accountDetails, setAccountDeails] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const [popup, setPopup] = useState({
    open: false,
    sip: null,
    loading: false,
    step: "",
    confirmMessage: "",
    cancelMessage: "",
  });
  const openPopup = useCallback(
    (next) => setPopup((p) => ({ ...p, open: true, ...next })),
    [],
  );
  const resetPopup = () =>
    setPopup({
      open: false,
      sip: null,
      loading: false,
      step: "",
      confirmMessage: "",
      cancelMessage: "",
    });
  const setPopupLoading = useCallback(
    (loading) => setPopup((p) => ({ ...p, loading })),
    [],
  );

  const account_token = GetCookie("token");
  const account_code = customer?.account_code;

  // Debug log
  useEffect(() => {
    console.log("Investments:", investment);
  }, [investment]);

  useEffect(() => {
    setLoading(true);
    setInvestment([]);
    fetchClientMasterReport();
    fetchSipDetails();
  }, [account_token, account_code]);

  useEffect(() => {
    const loadingMessageInterval = setInterval(() => {
      setLoadingMsg((prev) =>
        prev === "Fetching your SIPs"
          ? "It may take some time"
          : "Fetching your SIPs",
      );
    }, 3000);

    const opacityInterval = setInterval(() => {
      setOpacityMsg((prev) => !prev);
    }, 1500);

    return () => {
      clearInterval(loadingMessageInterval);
      clearInterval(opacityInterval);
    };
  }, []);

  const fetchSipDetails = async () => {
    try {
      let url = "";
      if (customer && account_code) {
        url = `/mf/ceasependingsip?account_code=${account_code}`;
      } else if (account_token) {
        url = `/mf/ceasependingsip?token=${account_token}`;
      } else {
        return;
      }

      const response = await ServerRequest({ url, method: "get" });

      // Handle both array direct or wrapped inside .results
      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.results)
          ? response.results
          : [];

      setInvestment(data);
    } catch (e) {
      console.error("SIP fetch error", e);
      setInvestment([]);
    } finally {
      setLoading(false);
    }
  };

  async function fetchClientMasterReport() {
    try {
      let url = "";
      if (customer && account_code) {
        url = `/nse/get-client-account?account_code=${account_code}`;
      } else if (account_token) {
        url = `/nse/get-client-account?token=${account_token}`;
      } else {
        return;
      }

      const res = await ServerRequest({ url, method: "get" });

      setAccountDeails(res?.data?.report_data || []);
    } catch (err) {
      console.error("Client master fetch error", err);
    }
  }

  const handleAuthEmail = async (h) => {
    setPopupLoading(true);
    openPopup({ open: true, isCancel: true });
    try {
      const response = await ServerRequest({
        url: `/mf/fetchceasesipbyceaseinternalreferenceno?internal_no=${h.cease_internal_order_id}`,
        method: "get",
      });
      setPopup((p) => ({ ...p, sip: response?.data, step: "Process" || [] }));
    } catch (e) {
      console.error("Cancel prefetch error", e);
    } finally {
      setPopupLoading(false);
    }
  };

  const handleSubmit = async () => {
    setPopupLoading(true);
    try {
      const res = await ServerRequest({
        url: "/nse/resendsipceasedauthlink",
        method: "post",
        data: {
          transactions: popup.sip,
          clientData: accountDetails,
        },
      });

      console.log(res);
      if (res.success === true) {
        setPopup((p) => ({
          ...p,
          step: "confirm",
          confirmMessage: "Success",
          cancelMessage: "SIP cancelled authorisation email sent successfully",
        }));
      } else {
        setPopup((p) => ({
          ...p,
          step: "confirm",
          confirmMessage: "Failed", // 👈 type
          cancelMessage:
            "Failed to send SIP cancellation authorisation email. Please try again.",
        }));
      }
    } catch (e) {
      console.error("Cancel prefetch error", e);
    } finally {
      setPopupLoading(false);
    }
  };

  return (
    <div className="mf-wrapper">
      {loading ? (
        <div className="app__login__screen">
          <div className="app__login__process">
            <Pulse />
            {/* <p className={opacityMsg ? "fadeIn" : "fadeOut"}>{loadingMsg}</p> */}
          </div>
        </div>
      ) : (
        <>
          {investment.length === 0 ? (
            <div className="mf-empty">
              <OrderBook size={40} color={COLOR_VARS["SWIFT_COLOR4"]} />
              <div>
                <p>No Transaction found</p>
                <span>Looks like you have't made any Transactions</span>
              </div>
            </div>
          ) : (
            <>
              <div
                className={`mf-container ${customer && "short"} mf-transaction-container mf-portfolio-container`}
              >
                {DEVICE_SIZE === "S" ? (
                  <>
                    {investment.map((h, index) => (
                      <InvestMentCard
                        key={index}
                        data={h}
                        isCustomer={customer}
                        handleAuthEmail={handleAuthEmail}
                        isActive={activeCard === index}
                        onActivate={() => setActiveCard(index)}
                        onDeactivate={() => setActiveCard(null)}
                      />
                    ))}
                  </>
                ) : (
                  <>
                    <table>
                      <thead>
                        <tr>
                          <td className="scheme-name">Scheme Name</td>
                          <td className="">ISIN</td>
                          <td className="">Folio No</td>
                          <td className="category">Category</td>
                          <td className="">Amount</td>
                          <td className="">Transaction Type</td>
                          <td className="">Completed</td>
                          <td>Frequencys</td>
                          <td className="">Day</td>
                          <td className="">Status</td>
                        </tr>
                      </thead>
                      <tbody>
                        {investment.map((h, index) => (
                          <tr
                            key={`active-${index}`}
                            className="yes_hover sip-table-row"
                          >
                            <td>{h["SchemeName"]}</td>
                            <td>{h["ISIN"]}</td>
                            <td>{h["FolioNo"]}</td>
                            <td className="body-category">{h["Category"]}</td>
                            <td>{priceFormat(h["Amount"])}</td>
                            <td>{h["TransactionType"]}</td>
                            <td>{h["Completed"]}</td>
                            <td>{h["Frequency"]}</td>
                            <td>{h["DayOfMonth"]}</td>
                            <td>
                              <Badge
                                variant={
                                  h.Status === "Completed"
                                    ? "success"
                                    : h.Status === "Approval Pending" ||
                                        h.Status === "Pending"
                                      ? "warning"
                                      : "error"
                                }
                                size="xxs"
                                style={{
                                  width: "max-content",
                                  height: "fit-content",
                                }}
                              >
                                {h.Status}
                              </Badge>
                            </td>
                            <td>
                              {customer && (
                                <div className="hover-container">
                                  {h.TransactionType === "Ceased SIP" && (
                                    <div
                                      className="hover-btn"
                                      onClick={() => {
                                        handleAuthEmail(h);
                                      }}
                                    >
                                      Resend Auth-Email
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}
      {popup.open && (
        <SwiftModal closeModal={resetPopup} top="2%">
          <div
            className={`${popup.step === "confirm" ? "confirm" : ""} swift-mf-popup`}
          >
            {popup.loading ? (
              <div className="swift-mf-popup-loader">
                <Pulse />
                <p>Processing...</p>
              </div>
            ) : (
              <>
                {popup.step === "confirm" ? (
                  <div className="swift-mf-popup-body">
                    <div className="swift-mf-popup-details">
                      <ConfirmationBoxSmart
                        confirmMessage={popup.confirmMessage}
                        cancelMessage={popup.cancelMessage}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="swift-mf-popup-header">
                      <div className="swift-mf-popup-header-title">
                        Resend Auth Email
                      </div>
                    </div>
                    <div className="swift-mf-popup-body">
                      <div className="swift-mf-popup-details">
                        {(popup.sip || []).map((order, index) => (
                          <div className="popup-card">
                            <p className="swift-mf-popup-isin">
                              {order.sip_registration_number}
                            </p>
                            <p className="swift-mf-popup-name">
                              {order.fund_name}
                            </p>
                            <p className="swift-mf-popup-isin">
                              {priceFormat(order.amount)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div className="swift-mf-popup-submit-btn">
                  {popup.step === "confirm" ? (
                    <CustomIconButton
                      title="Close"
                      icon={
                        <ArrowRight2
                          size={20}
                          color={COLOR_VARS.SWIFT_COLOR6}
                        />
                      }
                      onClick={resetPopup}
                    />
                  ) : (
                    <CustomIconButton
                      title="Submit"
                      icon={
                        <ArrowRight2
                          size={20}
                          color={COLOR_VARS.SWIFT_COLOR6}
                        />
                      }
                      onClick={handleSubmit}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </SwiftModal>
      )}
    </div>
  );
};

export default SipTransaction;

function InvestMentCard({
  data,
  isCustomer,
  handleAuthEmail,
  isActive,
  onActivate,
  onDeactivate,
}) {
  const {
    FolioNo,
    Category,
    SchemeName,
    Amount,
    Completed,
    Frequency,
    DayOfMonth,
    Status,
    TransactionType,
  } = data;

  const cardRef = useRef();

  useEffect(() => {
    function handleClickOutside(event) {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        onDeactivate();
      }
    }

    if (isActive) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isActive, onDeactivate]);

  return (
    // <div className="mf-watchlist-card"  onClick={onActivate} ref={cardRef}>
    //   <div className="mfwc-info">
    //     <div className="isin-price">
    //       <p className="isin-type">
    //         Folio: <span>{FolioNo}</span>
    //       </p>
    //       <p style={{ fontSize: "13px", fontWeight: "700" }}>{Status}</p>
    //     </div>
    //     <p className="name">
    //       {SchemeName}
    //     </p>
    //     <p className="category">
    //       {Category}
    //     </p>
    //   </div>
    //   <div className="mfwc-config">
    //     <div className="mfwc-config-row">
    //       <p>
    //         Amount
    //         <span>{priceFormat(Amount)}</span>
    //       </p>
    //       <p>
    //         Frequency
    //         <span>{Frequency}</span>
    //       </p>
    //     </div>
    //     <div className="mfwc-config-row">
    //       <p>
    //         Completed
    //         <span>
    //           {Completed}
    //         </span>
    //       </p>
    //       <p>
    //         Day of Month
    //         <span>
    //           {DayOfMonth}
    //         </span>
    //       </p>
    //     </div>
    //     <div className="mfwc-config-row">
    //       <p>
    //         Transaction Type
    //         <span>
    //           {TransactionType}
    //         </span>
    //       </p>
    //       <p></p>
    //     </div>
    //   </div>
    //   {isCustomer && (<div className="hover-wrapper">
    //     {isActive && (
    //       <div className="sp__hover__options">
    //         {TransactionType === "Ceased SIP" && (
    //           <div
    //             className="sp__hover__buttons cease"
    //             onClick={() => {
    //               handleAuthEmail(data);
    //             }}
    //           >
    //             Resend Auth-Email
    //           </div>
    //         )}
    //       </div>)}
    //   </div>
    //   )}
    // </div>

    <div className="mf-watchlist-card" onClick={onActivate} ref={cardRef}>
      <div className="mfwc-config">
        <div className="mfwc-config-row">
          <p></p>
          <p></p>
        </div>
        <div className="mfwc-config-row">
          <p className="">
            <p className="name">{SchemeName}</p>
            <p className="category">
              {Category}&nbsp;Folio:&nbsp;{FolioNo}
            </p>
          </p>
          <p className="value">
            {priceFormat(Amount)}{" "}
            <Badge
              variant={
                Status === "Completed"
                  ? "success"
                  : Status === "Approval Pending" || Status === "Pending"
                    ? "warning"
                    : "error"
              }
              size="xxxs"
            >
              {Status}
            </Badge>
          </p>
        </div>
        <div className="mfwc-config-row">
          <p>
            Completed -&nbsp;
            <span>{Completed}</span>
          </p>
          <p>
            Frequency -&nbsp;
            <span>{Frequency}</span>
          </p>
        </div>
        <div className="mfwc-config-row">
          <p>
            Day of Month -&nbsp;
            <span>{DayOfMonth}</span>
          </p>
          <p>
            Transaction Type -&nbsp;
            <span>{TransactionType}</span>
          </p>
        </div>
      </div>
      {isCustomer && (
        <div className="hover-wrapper">
          {isActive && (
            <div className="sp__hover__options">
              {TransactionType === "Ceased SIP" && (
                <div
                  className="sp__hover__buttons cease"
                  onClick={() => {
                    handleAuthEmail(data);
                  }}
                >
                  Resend Auth-Email
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
